import type Context from '@/context';
import type { LogicCircuitModel } from '@/models/LogicCircuitModel';
import type { GridViewModel } from '@/models/grid/GridViewModel';
export interface RuntimeAuthority {
  capturing: boolean;
  readonly pendingEdits?: number;
  readonly ownsRuntime: boolean;
  readonly running: boolean;
  readonly executingNativeEdit: boolean;
  inputAllowed(release?: boolean): boolean;
  closeForProject<T>(persistAndReplace: () => Promise<T>): Promise<T>;
  error: string;
  capture<T>(capture: () => T): Promise<T>;
  pause(): Promise<void>;
  edit(operation: () => void): Promise<void>;
  editCircuit?(operation: () => void): Promise<void>;
  input(id: import('@/models/logic/RuntimeHandles').NodeHandle): void;
  fail(error: unknown): void;
  dispose(): void;
}
export const authorities = new WeakMap<object, RuntimeAuthority>();
// Only authoring circuit/history boundaries preserve playback. Context/UI
// boundaries also use orderedEdit for navigation and dialogs and must stay paused.
const circuitEditKeys = new WeakSet<object>();
function editBoundary(owner: RuntimeAuthority, key: object, operation: () => void) {
  return circuitEditKeys.has(key) && owner.editCircuit
    ? owner.editCircuit(operation)
    : owner.edit(operation);
}
const editorBindings = new WeakMap<RuntimeAuthority, Set<object>>();
export const views = new WeakMap<object, GridViewModel>();
export function assertAuthoritativeCapture(logic: object) {
  const owner = authorities.get(logic);
  if (owner && !owner.capturing)
    throw Error('Worker owns runtime state. Await an authoritative save/export barrier.');
}
export function orderedEdit(key: object, operation: () => void) {
  const owner = authorities.get(key);
  if (!owner || owner.executingNativeEdit) return operation();
  void editBoundary(owner, key, operation).catch((error) => owner.fail(error));
}
export function authorityFor(context: Context | undefined) {
  return context && authorities.get(context);
}
export function bindEditor(context: Context, owner: RuntimeAuthority) {
  let keys = editorBindings.get(owner);
  if (!keys) {
    keys = new Set();
    editorBindings.set(owner, keys);
  }
  for (const chip of [context.editor.currentChip, ...context.chipStore.chips]) {
    const editor = context.chipStore.chipEditorMap.get(chip.id);
    if (!editor) continue;
    for (const circuit of [editor.logicCircuitEditor, editor.structureCircuitEditor]) {
      authorities.set(circuit.circuitModel, owner);
      authorities.set(circuit.historyModel, owner);
      circuitEditKeys.add(circuit.circuitModel);
      circuitEditKeys.add(circuit.historyModel);
      keys.add(circuit.circuitModel);
      keys.add(circuit.historyModel);
    }
  }
}
export function unbindEditor(owner: RuntimeAuthority) {
  for (const key of editorBindings.get(owner) ?? []) {
    if (authorities.get(key) === owner) authorities.delete(key);
  }
  editorBindings.delete(owner);
}
export function assertLogicCapture(logic: LogicCircuitModel) {
  assertAuthoritativeCapture(logic);
}
export const ownedNodes = new WeakMap<object, RuntimeAuthority>();
export const activeNodeOwners = new Set<RuntimeAuthority>();
export function assertGraphCapture(graph: { nodes: Map<any, any> }) {
  if (!activeNodeOwners.size) return;
  for (const node of graph.nodes.values()) {
    const owner = ownedNodes.get(node);
    if (owner && !owner.capturing)
      throw Error('Worker-owned graph requires authoritative capture');
  }
}

export function sourceInputAllowed(context: Context, release = false) {
  return authorityFor(context)?.inputAllowed(release) ?? true;
}
export async function orderedEditResult<T>(
  key: object,
  operation: () => T | Promise<T>,
): Promise<T> {
  const owner = authorities.get(key);
  if (!owner || owner.executingNativeEdit) return await operation();
  let result!: T | Promise<T>;
  await editBoundary(owner, key, () => {
    result = operation();
  });
  return await result;
}
